import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';
import { HamButtonComponent } from '../button/button.component';

export interface HamMenuItem {
    id: string;
    title: string;
    subtitle?: string | null;
    disabled?: boolean;
    /** Why the entry is disabled — its tooltip. A dimmed entry without a reason leaves the user guessing. */
    reason?: string | null;
    /** Set when the menu picks a value (a year): the current one carries a check. */
    selected?: boolean;
}

/** Distance between the button and the menu, and the menu's minimum distance to the window edge. */
const MENU_GAP = 6;
const WINDOW_MARGIN = 8;
const CHECK_ICON = 'url(assets/hamIcons/check.svg)';

/**
 * A `ham-button` that opens a menu below it — the button's chevron turns while the menu is open.
 *
 * The menu is a `hamFlyout`: it sits in the global flyout layer, so a scrolling container never cuts
 * it off, and a click outside closes it. On top of that this component places it at the button,
 * moves it above the button when there is no room below, follows scrolling, and closes on `Escape`.
 */
@Component({
    selector: 'ham-menu-button',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <ham-button
            #trigger
            class="ham-menu-button__trigger"
            aria-haspopup="menu"
            rightIcon="assets/hamIcons/chevron-down.svg"
            [variant]="variant"
            [size]="size"
            [icon]="icon"
            [lucideIcon]="lucideIcon"
            [disabled]="disabled"
            [attr.aria-expanded]="open"
            (buttonClick)="toggle()"
        >
            <ng-content></ng-content>
        </ham-button>

        <div
            #menu
            *ngIf="open"
            hamFlyout
            class="ham-menu-button__menu"
            role="menu"
            [position]="position"
            [style.width.px]="menuWidth"
            [attr.data-testid]="menuTestId"
            (closeFlyout)="close()"
        >
            <p class="ham-menu-button__notice" *ngIf="notice">{{ notice }}</p>
            <button
                *ngFor="let item of items; trackBy: trackById"
                type="button"
                class="ham-menu-button__item"
                [class.is-selected]="item.selected === true"
                [attr.role]="item.selected === undefined ? 'menuitem' : 'menuitemradio'"
                [attr.aria-checked]="item.selected === undefined ? null : item.selected"
                [attr.data-testid]="itemTestId"
                [attr.data-item-id]="item.id"
                [hamTooltip]="item.disabled && item.reason ? item.reason : ''"
                [tooltipClass]="tooltipClass"
                [disabled]="item.disabled === true"
                (click)="select(item)"
            >
                <span class="ham-menu-button__text">
                    <span class="ham-menu-button__title">{{ item.title }}</span>
                    <span class="ham-menu-button__sub" *ngIf="item.subtitle">{{ item.subtitle }}</span>
                </span>
                <i class="ham-menu-button__check" *ngIf="item.selected" aria-hidden="true" [ngStyle]="checkStyle"></i>
            </button>
        </div>
    `,
    styleUrls: ['./ham-menu-button.component.scss'],
})
export class HamMenuButtonComponent implements OnDestroy {
    @Input() items: readonly HamMenuItem[] = [];
    /** A sentence above the entries, e.g. why most of them are unavailable. */
    @Input() notice: string | null = null;
    @Input() variant: HamButtonComponent['variant'] = 'white';
    @Input() size: HamButtonComponent['size'] = 'toolbar';
    @Input() icon: string | null = null;
    @Input() lucideIcon: string | null = null;
    @Input() disabled = false;
    /** The button edge the menu lines up with. */
    @Input() align: 'start' | 'end' = 'end';
    @Input() menuWidth = 280;
    @Input() menuTestId: string | null = null;
    @Input() itemTestId: string | null = null;
    /** Look of the disabled-reason tooltip (`hamTooltip`'s `tooltipClass`), e.g. `abcp`. */
    @Input() tooltipClass: string | undefined;
    @Output() itemSelect = new EventEmitter<string>();

    @ViewChild('trigger', { read: ElementRef }) private trigger?: ElementRef<HTMLElement>;
    @ViewChild('menu') private menu?: ElementRef<HTMLElement>;

    public open = false;
    public position: { top: number; right: number; left: number } | null = null;
    public readonly checkStyle = { '-webkit-mask-image': CHECK_ICON, 'mask-image': CHECK_ICON };

    constructor(private readonly cdr: ChangeDetectorRef) {}

    public toggle(): void {
        if (this.open) {
            this.close();
            return;
        }
        if (this.disabled) {
            return;
        }
        this.open = true;
        this.place();
        window.addEventListener('scroll', this.onAnchorMoved, true);
        window.addEventListener('resize', this.onAnchorMoved);
        // The menu's height is only known once it is drawn — placed again so it can flip above.
        requestAnimationFrame(this.onAnchorMoved);
    }

    public close(): void {
        if (!this.open) {
            return;
        }
        this.open = false;
        this.position = null;
        this.stopFollowing();
        this.cdr.markForCheck();
    }

    public select(item: HamMenuItem): void {
        if (item.disabled === true) {
            return;
        }
        this.close();
        this.itemSelect.emit(item.id);
    }

    public trackById(_: number, item: HamMenuItem): string {
        return item.id;
    }

    @HostListener('document:keydown.escape')
    public onEscape(): void {
        this.close();
    }

    public ngOnDestroy(): void {
        this.stopFollowing();
    }

    private readonly onAnchorMoved = (): void => {
        if (this.open) {
            this.place();
        }
    };

    private stopFollowing(): void {
        window.removeEventListener('scroll', this.onAnchorMoved, true);
        window.removeEventListener('resize', this.onAnchorMoved);
    }

    private place(): void {
        const anchor = this.trigger?.nativeElement;
        if (anchor === undefined) {
            return;
        }
        const rect = anchor.getBoundingClientRect();
        const height = this.menu?.nativeElement.offsetHeight ?? 0;
        const below = rect.bottom + MENU_GAP;
        const above = rect.top - MENU_GAP - height;
        const top =
            height > 0 && below + height > window.innerHeight - WINDOW_MARGIN && above >= WINDOW_MARGIN ? above : below;
        const preferred = this.align === 'end' ? rect.right - this.menuWidth : rect.left;
        const left = Math.max(WINDOW_MARGIN, Math.min(preferred, window.innerWidth - this.menuWidth - WINDOW_MARGIN));

        this.position = { top, left, right: window.innerWidth - left - this.menuWidth };
        this.cdr.markForCheck();
    }
}
